// src/App.js
import React, { useState } from 'react';
import CouponsPanel from './components/CouponsPanel';
import AddBookForm from './components/AddBookForm';
import BooksTable from './components/BooksTable';
import YearlyBookStats from './components/YearlyBookStats';
import MonthlyBooksChart from './components/MonthlyBooksChart';
import StatsPanel from './components/StatsPanel';
import './theme.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Cache helpers
const getCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};
const setCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

function App() {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [couponRefresh, setCouponRefresh] = useState(false);
  const currentYear = new Date().getFullYear();
  // Default to most recent year with books, or current year
  const cachedBooks = getCache('books') || [];
  const yearsWithBooks = [...new Set(cachedBooks.map(b => new Date(b.date).getFullYear()))].sort((a, b) => b - a);
  const defaultYear = yearsWithBooks[0] || currentYear;
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [allBooks, setAllBooks] = useState(() => getCache('books') || []);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleExportCSV = () => {
    if (allBooks.length === 0) return;
    const headers = ['Title', 'Rating', 'Comments', 'Date'];
    const rows = allBooks.map(book => [
      `"${(book.title || '').replace(/"/g, '""')}"`,
      book.rating || '',
      `"${(book.comments || '').replace(/"/g, '""')}"`,
      book.date || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `anastasia-books-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Apply dark mode class to body
  React.useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const handleBookAdded = () => {
    // Refresh books, coupon count, and stats/chart after a book is added.
    setRefreshToggle(prev => !prev);
    setCouponRefresh(prev => !prev);
  };

  const handleCouponUsed = () => {
    setCouponRefresh(prev => !prev);
  };

  return (
    <div className="app-container">
      <div className="header-row">
        <h1>Anastasia Coupons {isUpdating && <span className="updating-badge">↻</span>}</h1>
        <button 
          className="dark-mode-toggle" 
          onClick={() => setDarkMode(!darkMode)}
          aria-label="Toggle dark mode"
        >
          {darkMode ? '☀️' : '🌙'}
        </button>
      </div>
      
      <div className="card">
        <CouponsPanel refreshCouponsTrigger={couponRefresh} onCouponUsed={handleCouponUsed} />
      </div>

      <div className="card">
        <StatsPanel refreshToggle={refreshToggle} />
      </div>
      
      <div className="card">
        <div className="year-selector">
          {Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i).map(year => (
            <button
              key={year}
              className={selectedYear === year ? 'year-btn active' : 'year-btn'}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
        <YearlyBookStats refreshToggle={refreshToggle} selectedYear={selectedYear} />
        <MonthlyBooksChart refreshToggle={refreshToggle} selectedYear={selectedYear} />
      </div>

      <div className="card">
        <h2>Add a Book</h2>
        <AddBookForm onBookAdded={handleBookAdded} />
      </div>
      
      <div className="card">
        <div className="section-header">
          <h2>Books List</h2>
          <button className="export-btn" onClick={handleExportCSV}>
            📥 Export CSV
          </button>
        </div>
        <BooksTable refreshToggle={refreshToggle} onBooksLoaded={setAllBooks} onUpdating={setIsUpdating} />
      </div>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default App;
